import { Suspense, useCallback, useEffect, useRef, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useOutletContext } from "react-router"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { ConnectionHandler, fetchQuery } from "relay-runtime"
import { useUser } from "@/apis/bff"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useToast } from "@/hooks/useToast"
import { DateOnly } from "@/lib/date-only"
import { cn } from "@/lib/utils"
import { useHealthInformationCopilotContext } from "@/modules/patients/contexts/health-information-copilot-context"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { DeleteMedicationButton } from "@/modules/patients/detail/medication/ch/delete-medication-button"
import { MedicationCreation } from "@/modules/patients/detail/medication/ch/medication-creation"
import { MedicationViewer } from "@/modules/patients/detail/medication/ch/medication-viewer"
import {
  MedicationSchema,
  MedicationSchemaInput,
  MedicationSchemaType,
} from "@/modules/patients/detail/medication/ch/schema/medication-schema"
import { chMedicationFragmentInline$data } from "@/modules/patients/detail/medication/ch/services/__generated__/chMedicationFragmentInline.graphql"
import { chVerifyMedicationQuery } from "@/modules/patients/detail/medication/ch/services/__generated__/chVerifyMedicationQuery.graphql"
import { useChCreateMedicationMutation } from "@/modules/patients/detail/medication/ch/services/chCreateMedicationMutation"
import { useChUpdateMedicationMutation } from "@/modules/patients/detail/medication/ch/services/chUpdateMedicationMutation"
import { ch_VerifyMedicationQuery } from "@/modules/patients/detail/medication/ch/services/chVerifyMedicationQuery"
import { useChDeleteMedicationMutation } from "@/modules/patients/detail/medication/ch/services/useChDeleteMedicationMutation"
import {
  ChMedicationSearch,
  MedicationDetail,
} from "@/modules/patients/detail/medication/components/ch-medication-search"
import { EmptyMedicationQuestion } from "@/modules/patients/detail/medication/components/empty-medication-question"
import { useGetMedicationVerification } from "@/modules/patients/detail/medication/hooks/useGetMedicationVerification"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications"
import { isFreeTextMedicationType } from "@/modules/patients/detail/medication/lib/is-free-text-medication-type"
import { useVerifyMedicationMutation } from "@/modules/patients/detail/medication/services/useVerifyMedicationMutation"
import { useChMedicationContext } from "@/modules/patients/detail/medication/store/useChMedicationStore"
import { ChMedicationFragmentInlineList } from "@/modules/patients/detail/medication/types/medication-fragment-type"
import { MEDICATION_MODES } from "@/modules/patients/detail/medication/types/medication-mode"
import { PRN_UNIT } from "@/modules/patients/detail/medication/types/medication-unit"
import {
  MEDICATION_VERIFICATION_STATUSES,
  NOT_ENQUIRED_STATUS,
} from "@/modules/patients/detail/medication/types/medication-verification-status"
import { ChPrescriptionDrawer } from "@/modules/patients/detail/prescription/ch/ch-prescription-drawer"
import { chPrescriptionFragment$key } from "@/modules/patients/detail/prescription/ch/services/__generated__/chPrescriptionFragment.graphql"
import { useChPrescriptionStoreContext } from "@/modules/patients/detail/prescription/ch/store/usePrescriptionStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientFragment$data } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { RelayEnvironment } from "@/relay-environment"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { VerifyMedicationStatus } from "@/types/__generated__/union-types"

const medicationFormDefaultValues: MedicationSchemaType = {
  medication: null,
  preparation: null,
  unit: "PC",
  morningDosage: "",
  noonDosage: "",
  eveningDosage: "",
  nightDosage: "",
  internalNotes: "",
  usageInstructions: "",
  freeTextMedicationName: "",
  numberOfPackages: 1,
  refuseSubstitutes: false,
}

type MedicationProps = {
  editMedication?: chMedicationFragmentInline$data
  freeTextMedicationName?: string
  medication?: MedicationDetail
}

type ChMedicationDrawerProps = {
  recommendedMedication?: MedicationProps & { onAddSuccess: (id: string) => void; progressNoteId: string }
  prescriptionMedication?: MedicationProps & {
    progressNoteId: string
    prescriptionRef?: chPrescriptionFragment$key
    id?: string
  }
  diagnosisCode?: string
}

const fetchVerifyMedicationData = (input: chVerifyMedicationQuery["variables"]) => {
  fetchQuery<chVerifyMedicationQuery>(RelayEnvironment, ch_VerifyMedicationQuery, input).toPromise()
}

const ChMedicationDrawer = ({
  prescriptionMedication,
  recommendedMedication,
  diagnosisCode,
}: ChMedicationDrawerProps) => {
  const user = useUser()
  const hasSavePermission = user.isOneOf(["Doctor", "MedicalSupervisor", "NursingProfessional"])
  const { t } = useTranslation()
  const { patient } = useOutletContext<{ patient: PatientFragment$data }>()
  const { medgateServiceInfo } = useOutletContext<{ medgateServiceInfo: { genericsObligation: boolean } }>()
  const isAddingFreeTextMedicationRef = useRef(
    !!recommendedMedication?.freeTextMedicationName || !!prescriptionMedication?.freeTextMedicationName,
  )
  const [medicationSearchText, setMedicationSearchText] = useState<string | null>(null)
  /*
   * recommendedMedicationRef is used to track data processing value from Recommended Medication
   */
  const recommendedMedicationRef = useRef(false)
  const prescriptionMedicationRef = useRef(false)
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const chMedicationMode = useChMedicationContext((state) => state.chMedicationMode)
  const prevChMode = useChMedicationContext((state) => state.prevChMode)
  const chooseSearchItem = useChMedicationContext((state) => state.chooseSearchItem)
  const setChMedicationDrawerMode = useChMedicationContext((state) => state.setChMedicationDrawerMode)
  const resetChMedicationStore = useChMedicationContext((state) => state.reset)
  const selectExistingMedication = useChMedicationContext((state) => state.selectExistingMedication)
  const existingMedication = useChMedicationContext((state) => state.existingMedication)
  const openDrawerWithoutConfirmation = useDrawerStore((state) => state.openDrawerWithoutConfirmation)
  const setUnSavedMedicationIds = useChPrescriptionStoreContext((state) => state.setUnSavedMedicationIds)
  const removeUnSavedMedicationId = useChPrescriptionStoreContext((state) => state.removeUnSavedMedicationId)
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)
  const { toast } = useToast()
  const [deleteChMedication] = useChDeleteMedicationMutation()

  const selectedCopilotMedication = useHealthInformationCopilotContext((state) => state.medications.selected)
  const setSelectedCopilotMedication = useHealthInformationCopilotContext((state) => state.medications.setSelected)
  const resetSelectedCopilotMedication = useHealthInformationCopilotContext((state) => state.medications.resetSelected)

  const form = useForm<MedicationSchemaInput, unknown, MedicationSchemaType>({
    mode: "all",
    resolver: zodResolver(MedicationSchema),
    defaultValues: {
      ...medicationFormDefaultValues,
      freeTextMedicationName: recommendedMedication?.freeTextMedicationName ?? "",
    },
  })
  const patientId = usePatientParam()
  const [createMedication, isCreating] = useChCreateMedicationMutation()
  const [updateMedication, isUpdating] = useChUpdateMedicationMutation()
  const verifyMedicationMutation = useVerifyMedicationMutation()
  const isMedicationEditMode = existingMedication?.id
  const isAddMode = !isMedicationEditMode && patientId

  const appointmentId = useAppointmentParam()
  const isEditMode = useEditMode()
  const { activeMedications, inactiveMedications } = useMedications<ChMedicationFragmentInlineList>()
  const { medicationVerificationData } = useGetMedicationVerification()
  const {
    lastVerifyMedicationStatus: verifyMedicationStatus,
    lastVerifiedAt: verifiedAt,
    lastVerifiedBy: verifiedBy,
  } = medicationVerificationData || {}
  const [verificationStatus, setVerificationStatus] = useState<VerifyMedicationStatus>(
    verifyMedicationStatus && MEDICATION_VERIFICATION_STATUSES.includes(verifyMedicationStatus)
      ? verifyMedicationStatus
      : NOT_ENQUIRED_STATUS,
  )

  const {
    setValue,
    formState: { dirtyFields },
    reset,
    getValues,
  } = form
  const isDirty = Object.keys(dirtyFields).length > 0

  const redirectToPrescription = useCallback(() => {
    if (!prescriptionMedication) return

    resetChMedicationStore()
    const drawerContent = diagnosisCode ? (
      <ChPrescriptionDrawer
        progressNoteId={prescriptionMedication.progressNoteId}
        prescriptionRef={prescriptionMedication.prescriptionRef}
        id={prescriptionMedication.id}
        diagnosisCode={diagnosisCode}
      />
    ) : (
      <div></div>
    )
    openDrawerWithoutConfirmation(() => drawerContent, diagnosisCode ? "PrescriptionDrawer" : undefined)
  }, [prescriptionMedication, openDrawerWithoutConfirmation, resetChMedicationStore, diagnosisCode])

  const handleDrawerChange = useCallback(() => {
    isAddingFreeTextMedicationRef.current = false
    if (prescriptionMedication) {
      redirectToPrescription()
      return
    }

    resetChMedicationStore()

    if (prevChMode === MEDICATION_MODES.VIEW && chMedicationMode !== MEDICATION_MODES.VIEW) {
      setChMedicationDrawerMode(MEDICATION_MODES.VIEW)
      FaroInsightsWrapper.pushEvent("healthInformation_ch_medication_cancel")
      return
    }

    closeDrawer()
    resetSelectedCopilotMedication()
    FaroInsightsWrapper.pushEvent("healthInformation_ch_medication_close")
  }, [
    chMedicationMode,
    closeDrawer,
    prescriptionMedication,
    prevChMode,
    redirectToPrescription,
    resetChMedicationStore,
    setChMedicationDrawerMode,
    resetSelectedCopilotMedication,
  ])

  useEffect(() => {
    if (chMedicationMode === MEDICATION_MODES.VIEW) {
      reset({ ...medicationFormDefaultValues, freeTextMedicationName: recommendedMedication?.freeTextMedicationName })
    }
  }, [chMedicationMode, recommendedMedication?.freeTextMedicationName, reset])

  const handleFreeTextMedicationSelection = (inputValue: string) => {
    isAddingFreeTextMedicationRef.current = true
    reset(medicationFormDefaultValues)
    setValue("freeTextMedicationName", inputValue)
    chooseSearchItem()
  }

  useEffect(() => {
    if (selectedCopilotMedication) {
      setMedicationSearchText(selectedCopilotMedication)
      setSelectedCopilotMedication(null)
    }
  }, [selectedCopilotMedication, chMedicationMode, setSelectedCopilotMedication, setMedicationSearchText])

  const setFormFieldsFromMedicationTemplate = useCallback(
    (medication: MedicationDetail) => {
      const defaultValue = prescriptionMedicationRef.current ? "" : 0

      if (medication.medicationType !== "TEMPLATE") {
        setValue("asNeeded", false)
        setValue("unit", medicationFormDefaultValues.unit)
        setValue("morningDosage", `${defaultValue}`)
        setValue("noonDosage", `${defaultValue}`)
        setValue("eveningDosage", `${defaultValue}`)
        setValue("nightDosage", `${defaultValue}`)
        setValue("refuseSubstitutes", false)
        return
      }

      // If the medication is already set, the toast that informs data is overridden will be triggered.
      // Except for the first time when the drawer is opened (medication is null).
      if (getValues("medication")) {
        toast({
          description: t("patient.medication.medicationInformationOverridden"),
        })
      }

      const unit = medication.unit ?? medicationFormDefaultValues.unit
      const today = new DateOnly(new Date())

      setValue("asNeeded", unit === PRN_UNIT)
      setValue("unit", unit)
      setValue("morningDosage", `${medication.morningDosage ?? defaultValue}`)
      setValue("noonDosage", `${medication.noonDosage ?? defaultValue}`)
      setValue("eveningDosage", `${medication.eveningDosage ?? defaultValue}`)
      setValue("nightDosage", `${medication.nightDosage ?? defaultValue}`)
      setValue("usageInstructions", medication.usageInstructions ?? null)
      setValue("startDate", today.toDate())
      setValue(
        "endDate",
        medication.therapyDurationDays ? today.addDays(medication.therapyDurationDays).toDate() : undefined,
      )
      setValue("refuseSubstitutes", false)
    },
    [setValue, getValues, toast, t],
  )

  const handleSubmit = (data: MedicationSchemaType) => {
    const {
      startDate,
      endDate,
      unit,
      morningDosage,
      noonDosage,
      eveningDosage,
      nightDosage,
      internalNotes,
      usageInstructions,
      medication,
      preparation,
      freeTextMedicationName,
      refuseSubstitutes,
      numberOfPackages,
      asNeeded,
      isPermanentPrescription,
    } = data

    const connectionID = ConnectionHandler.getConnectionID(patientId, "PatientByIdConnection_ch_medications")

    const medicationName = freeTextMedicationName || (medication?.name ?? "")
    const articles = medication?.articles ?? []
    const selectedArticle = preparation ?? articles[0]

    const payload = {
      name: medicationName,
      pharmacode: selectedArticle?.pharmacode,
      globalTradeItemNumber: selectedArticle?.globalTradeItemNumber,
      startDate: startDate ? new DateOnly(startDate).toString() : undefined,
      endDate: endDate ? new DateOnly(endDate).toString() : undefined,
      morningDosage: asNeeded ? 0 : Number(morningDosage),
      noonDosage: asNeeded ? 0 : Number(noonDosage),
      eveningDosage: asNeeded ? 0 : Number(eveningDosage),
      nightDosage: asNeeded ? 0 : Number(nightDosage),
      unit,
      internalNotes,
      usageInstructions,
      templateId: medication?.medicationType === "TEMPLATE" ? medication.itemId : null,
      templateCategory: medication?.category,
      type: medication?.medicationType ?? "FREE_TEXT_MEDICATION",
      packageSize: preparation?.description,
      refuseSubstitutes,
      numberOfPackages,
      isPermanentPrescription: !!isPermanentPrescription,
    }

    if (isAddMode) {
      createMedication({
        variables: {
          input: {
            patientId,
            name: payload.name,
            pharmacode: payload.pharmacode,
            globalTradeItemNumber: payload.globalTradeItemNumber,
            startDate: payload.startDate,
            endDate: payload.endDate,
            morningDosage: payload.morningDosage,
            noonDosage: payload.noonDosage,
            eveningDosage: payload.eveningDosage,
            nightDosage: payload.nightDosage,
            unit: payload.unit,
            internalNotes: payload.internalNotes,
            usageInstructions: payload.usageInstructions,
            templateId: payload.templateId,
            templateCategory: payload.templateCategory,
            type: payload.type,
            packageSize: payload.packageSize,
            refuseSubstitutes: payload.refuseSubstitutes,
            numberOfPackages: payload.numberOfPackages,
            isPermanentPrescription: payload.isPermanentPrescription,
          },
          connections: [connectionID],
        },
        onCompleted(response) {
          const id = response.ch_addMedication.ch_Medication?.id
          if (id) {
            if (prescriptionMedication) {
              redirectToPrescription()
              setUnSavedMedicationIds(id)
              return
            }
            if (recommendedMedication) {
              recommendedMedication.onAddSuccess(id)
            }
            handleDrawerChange()
          }
        },
      })
    }

    if (isMedicationEditMode) {
      updateMedication({
        variables: {
          input: {
            id: existingMedication.id,
            name: payload.name,
            pharmacode: payload.pharmacode,
            globalTradeItemNumber: payload.globalTradeItemNumber,
            startDate: payload.startDate,
            endDate: payload.endDate,
            morningDosage: payload.morningDosage,
            noonDosage: payload.noonDosage,
            eveningDosage: payload.eveningDosage,
            nightDosage: payload.nightDosage,
            unit: payload.unit,
            internalNotes: payload.internalNotes,
            usageInstructions: payload.usageInstructions,
            templateId: payload.templateId,
            templateCategory: payload.templateCategory,
            packageSize: payload.packageSize,
            refuseSubstitutes: payload.refuseSubstitutes,
            numberOfPackages: payload.numberOfPackages,
            isPermanentPrescription: payload.isPermanentPrescription,
          },
          connections: [connectionID],
        },
        onCompleted(response) {
          const id = response.ch_updateMedication.ch_Medication?.id
          if (id) {
            if (prescriptionMedication) {
              redirectToPrescription()
              return
            }
            handleDrawerChange()
          }
        },
      })
    }
  }

  const onSubmit = (data: MedicationSchemaType) => {
    if (medgateServiceInfo.genericsObligation && data.refuseSubstitutes) {
      confirm({
        title: t("common.confirmation.title"),
        content: t("common.confirmation.confirmToRefuseGenericWithObligation"),
        confirmText: t("common.confirmation.save"),
        cancelText: t("common.button.cancel"),
        confirmVariant: "primary",
        onConfirm: () => {
          handleSubmit(data)
          cancel()
        },
      })
      return
    }

    handleSubmit(data)
  }

  const resolveArticleFromStoredPharmacode = useCallback(
    (medication: MedicationDetail, storedPharmacode: number | null | undefined) =>
      (typeof storedPharmacode === "number"
        ? medication.articles?.find((article) => article.pharmacode === storedPharmacode)
        : undefined) ??
      medication.articles?.[0] ??
      null,
    [],
  )

  const handleSelectExistingMedication = useCallback(
    (item: chMedicationFragmentInline$data, medication?: MedicationDetail) => {
      const {
        name,
        morningDosage,
        noonDosage,
        eveningDosage,
        nightDosage,
        startDate,
        endDate,
        unit,
        usageInstructions,
        internalNotes,
        type,
        refuseSubstitutes,
        numberOfPackages,
        pharmaCode,
        isPermanentPrescription,
        templateId,
        productUrl,
      } = item

      const resolvedArticle = medication ? resolveArticleFromStoredPharmacode(medication, pharmaCode) : null

      reset({
        morningDosage: morningDosage.toString(),
        noonDosage: noonDosage.toString(),
        eveningDosage: eveningDosage.toString(),
        nightDosage: nightDosage.toString(),
        startDate: startDate ? new Date(startDate) : undefined,
        endDate: endDate ? new Date(endDate) : undefined,
        unit,
        usageInstructions: usageInstructions ?? "",
        internalNotes: internalNotes ?? "",
        freeTextMedicationName: isFreeTextMedicationType(type) ? name : "",
        refuseSubstitutes,
        numberOfPackages: numberOfPackages ?? 1,
        asNeeded: unit === PRN_UNIT,
        globalTradeItemNumber: resolvedArticle?.pharmacode?.toString(),
        preparation: resolvedArticle,
        isPermanentPrescription: !!isPermanentPrescription,
      })
      selectExistingMedication(item)

      const medicationData = {
        name: name,
        medicationType: type,
        articles: medication?.articles,
        productUrl: productUrl ?? medication?.productUrl,
        hasNews: medication?.hasNews,
      }

      switch (type) {
        case "SUBSTANCE": {
          const medication = { ...medicationData, itemId: pharmaCode ?? undefined }
          setValue("medication", medication)
          break
        }
        case "TEMPLATE": {
          const medication = { ...medicationData, pharmaCode, itemId: templateId ?? undefined }
          setValue("medication", medication)
          break
        }
        case "PREPARATION": {
          const medication = { ...medicationData, pharmaCode, itemId: pharmaCode ?? undefined }
          setValue("medication", medication)
          break
        }
        case "FREE_TEXT_MEDICATION": {
          if (medication) {
            setValue("medication", medication)
          }
          isAddingFreeTextMedicationRef.current = true
        }
      }
    },
    [reset, selectExistingMedication, setValue, resolveArticleFromStoredPharmacode],
  )

  useEffect(() => {
    if (!prescriptionMedication || prescriptionMedicationRef.current) return

    prescriptionMedicationRef.current = true

    const { editMedication, medication, freeTextMedicationName } = prescriptionMedication
    if (medication) {
      const resolvedArticle = resolveArticleFromStoredPharmacode(medication, editMedication?.pharmaCode)
      setFormFieldsFromMedicationTemplate(medication)
      setValue("medication", medication)
      setValue("globalTradeItemNumber", resolvedArticle?.pharmacode?.toString())
      setValue("preparation", resolvedArticle)
    }
    if (editMedication) handleSelectExistingMedication(editMedication, medication)

    if (freeTextMedicationName) {
      setValue("freeTextMedicationName", freeTextMedicationName)
      isAddingFreeTextMedicationRef.current = true
    }
  }, [
    prescriptionMedication,
    setValue,
    handleSelectExistingMedication,
    setFormFieldsFromMedicationTemplate,
    resolveArticleFromStoredPharmacode,
  ])

  useEffect(() => {
    if (!recommendedMedication || recommendedMedicationRef.current) return

    recommendedMedicationRef.current = true

    const { editMedication, medication, freeTextMedicationName } = recommendedMedication
    if (medication) {
      const resolvedArticle = resolveArticleFromStoredPharmacode(medication, editMedication?.pharmaCode)
      setFormFieldsFromMedicationTemplate(medication)
      setValue("medication", medication)
      setValue("globalTradeItemNumber", resolvedArticle?.pharmacode?.toString())
      setValue("preparation", resolvedArticle)
    }
    if (editMedication) handleSelectExistingMedication(editMedication, medication)
    if (freeTextMedicationName) {
      setValue("freeTextMedicationName", freeTextMedicationName)
      isAddingFreeTextMedicationRef.current = true
    }
  }, [
    recommendedMedication,
    setValue,
    handleSelectExistingMedication,
    setFormFieldsFromMedicationTemplate,
    resolveArticleFromStoredPharmacode,
  ])

  const handleVerifyMedication = () => {
    if (!appointmentId) return

    verifyMedicationMutation(
      {
        appointmentId,
        patientId,
        verifyMedicationStatus:
          activeMedications.length > 0 || inactiveMedications.length > 0 ? "SOME" : verificationStatus,
      },
      () => {
        fetchVerifyMedicationData({ appointmentId, patientId, skip: false })
        closeDrawer()
      },
    )
  }

  useDirtyDrawerMaker(isDirty, handleDrawerChange)

  const onDelete = () => {
    if (!existingMedication?.id) return
    const connectionID = ConnectionHandler.getConnectionID(patientId, "PatientByIdConnection_ch_medications")
    deleteChMedication({
      variables: {
        input: {
          id: existingMedication.id,
        },
        connections: [connectionID],
      },
      onCompleted() {
        toast({
          description: t("patient.medication.deleteMessage.success"),
        })
        removeUnSavedMedicationId(existingMedication.id)
        cancel()
        handleDrawerChange()
      },
    })
  }

  const handleDelete = () => {
    confirm({
      title: `${t("common.button.delete")} ${existingMedication?.name}`,
      content: t("common.message.cannotUndoAction"),
      confirmIcon: TrashSimple,
      onConfirm: onDelete,
      confirmVariant: "danger",
      confirmText: t("common.button.delete"),
      cancelText: t("common.button.keep"),
    })
  }

  const handleSelectMedication = (medication: MedicationDetail) => {
    const resolvedArticle = resolveArticleFromStoredPharmacode(medication, undefined)
    setFormFieldsFromMedicationTemplate(medication)
    setValue("medication", medication)
    setValue("globalTradeItemNumber", resolvedArticle?.pharmacode?.toString())
    setValue("preparation", resolvedArticle)
    setChMedicationDrawerMode(MEDICATION_MODES.CREATION)
  }

  const isSearchable =
    chMedicationMode !== MEDICATION_MODES.EDIT && isEditMode && !isAddingFreeTextMedicationRef.current

  const includeTemplates = !!prescriptionMedication || !!recommendedMedication

  const handleSearchAtcCode = () => {
    const atcCode = getValues("medication.atcCode")
    if (atcCode) {
      setMedicationSearchText(atcCode)
    }
  }

  return (
    <Drawer open onOpenChange={handleDrawerChange}>
      <FormProvider {...form}>
        <DrawerContent data-testid="ch-medication-drawer-content">
          <DrawerClose className="top-2 right-2" />
          <DrawerHeader className="pr-4">
            <DrawerTitle
              key={chMedicationMode}
              className={cn(
                "animate-fade-in-bottom text-on-surface-subdued text-sm font-medium",
                includeTemplates && "mb-4",
              )}>
              {chMedicationMode === MEDICATION_MODES.CREATION && t("patient.medication.addMedication")}
              {(chMedicationMode === MEDICATION_MODES.VIEW || chMedicationMode === MEDICATION_MODES.EDIT) &&
                t("patient.medication.medication")}
            </DrawerTitle>
            {isSearchable && (
              <ChMedicationSearch
                medicationSearchText={medicationSearchText}
                setMedicationSearchText={setMedicationSearchText}
                className="mt-4"
                includeTemplates={includeTemplates}
                usePopover={includeTemplates}
                patientLanguage={patient.primaryLanguage}
                handleSelectMedication={handleSelectMedication}
                handleFreeTextMedicationSelection={handleFreeTextMedicationSelection}
                handleSelectExistingMedication={handleSelectExistingMedication}
                patientId={patientId}
                appointmentId={appointmentId}
              />
            )}
          </DrawerHeader>
          <DrawerBody className="py-0">
            {chMedicationMode === MEDICATION_MODES.VIEW && (
              <>
                {activeMedications.length === 0 && inactiveMedications.length === 0 && isEditMode && (
                  <EmptyMedicationQuestion
                    verificationStatus={verificationStatus}
                    setVerificationStatus={setVerificationStatus}
                  />
                )}
                <MedicationViewer
                  key={chMedicationMode}
                  patientLanguage={patient.primaryLanguage}
                  handleSelectExistingMedication={handleSelectExistingMedication}
                />
              </>
            )}
            {(chMedicationMode === MEDICATION_MODES.CREATION || chMedicationMode === MEDICATION_MODES.EDIT) && (
              <MedicationCreation
                key={chMedicationMode}
                chMedicationMode={chMedicationMode}
                isAddingFreeTextMedication={isAddingFreeTextMedicationRef.current}
                existingMedication={existingMedication}
                handleSearchMedicationText={handleSearchAtcCode}
              />
            )}
          </DrawerBody>
          <DrawerFooter>
            <div className="flex w-full flex-wrap justify-between gap-2">
              {chMedicationMode === MEDICATION_MODES.VIEW && verifiedAt && verifiedBy && (
                <LastModificationInfo
                  updatedAt={verifiedAt}
                  updatedBy={verifiedBy}
                  data-testid="medication-last-verified-info"
                  displayedText={t("common.confirmedOn")}
                />
              )}
              {isMedicationEditMode && chMedicationMode === MEDICATION_MODES.EDIT && (
                <div className="flex gap-1">
                  <LastModificationInfo
                    updatedAt={existingMedication?.updatedAt ?? existingMedication.createdAt}
                    updatedBy={existingMedication?.updatedBy ?? existingMedication.createdBy}
                    displayedText={existingMedication?.updatedAt ? t("common.updatedOn") : t("common.createdOn")}
                  />
                  {isEditMode && (
                    <Suspense fallback={null}>
                      <DeleteMedicationButton id={existingMedication.id} onClick={handleDelete} />
                    </Suspense>
                  )}
                </div>
              )}
              {(chMedicationMode === MEDICATION_MODES.CREATION || chMedicationMode === MEDICATION_MODES.EDIT) && (
                <div className="ml-auto flex gap-2">
                  <Button variant="secondary" onClick={handleDrawerChange}>
                    {t("common.button.cancel")}
                  </Button>
                  {isEditMode && (
                    <Button
                      isLoading={isCreating || isUpdating}
                      onClick={() => {
                        FaroInsightsWrapper.pushEvent("healthInformation_ch_medication_save")
                        form.handleSubmit(onSubmit)()
                      }}
                      disabled={!hasSavePermission || (!!isMedicationEditMode && !isDirty)}
                      data-testid="medication-save-button">
                      {t("common.button.save")}
                    </Button>
                  )}
                </div>
              )}
              {chMedicationMode === MEDICATION_MODES.VIEW && (
                <div className="ml-auto flex gap-2">
                  <Button variant="secondary" onClick={handleDrawerChange}>
                    {t("common.button.close")}
                  </Button>
                  {isEditMode && appointmentId && (
                    <Button
                      onClick={() => {
                        FaroInsightsWrapper.pushEvent("healthInformation_ch_medication_verify")
                        handleVerifyMedication()
                      }}
                      disabled={!hasSavePermission}
                      data-testid="medication-verify-button">
                      {t("common.button.verify")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </DrawerFooter>
        </DrawerContent>
      </FormProvider>
    </Drawer>
  )
}

export { ChMedicationDrawer }
